import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Panel } from '../components/Panel';

export function LoginPage() {
  const { state, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('erzinger');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, tenantSlug || undefined);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Falha ao autenticar');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background radial glow */}
      <div aria-hidden style={{
        position: 'absolute', top: '-20%', left: '50%',
        transform: 'translateX(-50%)',
        width: '800px', height: '500px',
        background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.025) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Fine grid texture */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(var(--border) 1px, transparent 1px),
          linear-gradient(90deg, var(--border) 1px, transparent 1px)
        `,
        backgroundSize: '48px 48px',
        pointerEvents: 'none',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 0%, black 0%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 0%, black 0%, transparent 100%)',
      }} />

      <div style={{ width: '100%', maxWidth: '440px', position: 'relative', zIndex: 1 }} className="animate-scale-in">

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '44px' }}>
          {/* Logo mark */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '16px',
            background: 'var(--t1)', margin: '0 auto 22px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 1px var(--border-strong), var(--shadow-lg)',
          }}>
            <svg width="30" height="30" viewBox="0 0 18 18" fill="none">
              <path d="M3 4h12M3 9h8M3 14h12" stroke="var(--bg)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--t1)', margin: '0 0 8px' }}>
            Compasso
          </h1>
          <p style={{ fontSize: '15px', color: 'var(--t2)', margin: 0 }}>
            Planeje no traço, execute no ritmo · Erzinger Engenharia
          </p>
        </div>

        {/* Form card */}
        <Panel>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="field">
              <label htmlFor="tenantSlug" className="field-label">Organização</label>
              <input
                id="tenantSlug"
                type="text"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                autoComplete="organization"
                className="input input-lg"
                placeholder="erzinger"
              />
            </div>

            <div className="field">
              <label htmlFor="email" className="field-label">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="input input-lg"
                placeholder="operador@erzinger.com.br"
              />
            </div>

            <div className="field">
              <label htmlFor="password" className="field-label">Senha</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                minLength={8}
                className="input input-lg"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="alert alert-error" role="alert">
                <span>⚠</span>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary btn-xl btn-block"
              style={{ marginTop: '8px', letterSpacing: '-0.01em' }}
            >
              {submitting ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="animate-spin" style={{ width: '16px', height: '16px', border: '2px solid var(--surface-3)', borderTopColor: 'var(--bg)', borderRadius: '50%', display: 'inline-block' }} />
                  Entrando...
                </span>
              ) : 'Entrar'}
            </button>
          </form>
        </Panel>

        {/* Footer */}
        <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--t3)', marginTop: '26px' }}>
          Sistema de Apontamento de Horas · Rev. 8
        </p>
      </div>
    </div>
  );
}
